import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Avatar,
  Button,
  Form,
  Input,
  Typography,
  Space,
  Tag,
  Pagination,
  App,
  Modal,
} from 'antd';
import {
  UserOutlined,
  LikeOutlined,
  DislikeOutlined,
  MessageOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '../hooks/redux';
import { apiClient } from '../services/api';
import type { ApiResponse } from '../services/api';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface Review {
  id: number;
  userId: number;
  brandId: number;
  content: string;
  pros: string[];
  cons: string[];
  createdAt: string;
  updatedAt: string;
  likes: number;
  dislikes: number;
  userLiked?: boolean;
  userDisliked?: boolean;
  user: {
    id: number;
    username: string;
    avatar?: string;
  };
}

interface UserReviewsProps {
  brandId: number;
}

const UserReviews: React.FC<UserReviewsProps> = ({ brandId }) => {
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();
  const { message, modal } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    loadReviews(currentPage);
  }, [brandId, currentPage]);

  const loadReviews = async (page: number) => {
    setLoading(true);
    try {
      const endpoint = `/brands/${brandId}/reviews?page=${page}&limit=10`;
      const response = await apiClient.get<ApiResponse<Review[]>>(endpoint);
      setReviews(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      message.error(t('user_reviews.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (values: {
    content?: string;
    pros?: string;
    cons?: string;
  }) => {
    try {
      const reviewData = {
        brandId,
        content: values.content,
        pros: values.pros
          ? values.pros.split('\n').filter((p: string) => p.trim())
          : [],
        cons: values.cons
          ? values.cons.split('\n').filter((c: string) => c.trim())
          : [],
      };

      if (editingReview) {
        await apiClient.put<ApiResponse<Review>>(
          `/reviews/${editingReview.id}`,
          reviewData
        );
        message.success(t('user_reviews.update_success'));
      } else {
        await apiClient.post<ApiResponse<Review>>(
          `/brands/${brandId}/reviews`,
          reviewData
        );
        message.success(t('user_reviews.publish_success'));
      }

      setReviewModalVisible(false);
      setEditingReview(null);
      form.resetFields();
      setCurrentPage(1);
      await loadReviews(1);
    } catch (error) {
      message.error(t('user_reviews.submit_failed'));
    }
  };

  const handleLikeReview = async (reviewId: number, isLike: boolean) => {
    if (!isAuthenticated) {
      message.warning(t('user_reviews.login_required'));
      return;
    }

    try {
      await apiClient.post(
        `/reviews/${reviewId}/${isLike ? 'like' : 'dislike'}`
      );
      loadReviews(currentPage);
    } catch (error) {
      message.error(t('user_reviews.action_failed'));
    }
  };

  const handleDeleteReview = async (reviewId: number) => {
    modal.confirm({
      title: t('user_reviews.delete_confirm_title'),
      content: t('user_reviews.delete_confirm_content'),
      onOk: async () => {
        try {
          await apiClient.delete(`/reviews/${reviewId}`);
          message.success(t('user_reviews.delete_success'));
          loadReviews(currentPage);
        } catch (error) {
          message.error(t('user_reviews.delete_failed'));
        }
      },
    });
  };

  const openReviewModal = (review?: Review) => {
    if (review) {
      setEditingReview(review);
      form.setFieldsValue({
        content: review.content,
        pros: review.pros.join('\n'),
        cons: review.cons.join('\n'),
      });
    } else {
      setEditingReview(null);
      form.resetFields();
    }
    setReviewModalVisible(true);
  };

  const renderReviewItem = (review: Review) => {
    const isOwner = user?.id === review.userId;

    return (
      <List.Item
        key={review.id}
        actions={[
          <Button
            type="text"
            icon={<LikeOutlined />}
            onClick={() => handleLikeReview(review.id, true)}
            style={{ color: review.userLiked ? '#1890ff' : undefined }}
          >
            {review.likes}
          </Button>,
          <Button
            type="text"
            icon={<DislikeOutlined />}
            onClick={() => handleLikeReview(review.id, false)}
            style={{ color: review.userDisliked ? '#ff4d4f' : undefined }}
          >
            {review.dislikes}
          </Button>,
          ...(isOwner
            ? [
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openReviewModal(review)}
                >
                  编辑
                </Button>,
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteReview(review.id)}
                >
                  删除
                </Button>,
              ]
            : []),
        ]}
      >
        <List.Item.Meta
          avatar={<Avatar src={review.user.avatar} icon={<UserOutlined />} />}
          title={
            <div>
              <Space>
                <Text strong>{review.user.username}</Text>
                <Text type="secondary">
                  {new Date(review.createdAt).toLocaleDateString()}
                </Text>
              </Space>
            </div>
          }
          description={
            <div>
              <Paragraph style={{ marginBottom: 12 }}>
                {review.content}
              </Paragraph>

              {review.pros.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ color: '#52c41a' }}>
                    优点：
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    {review.pros.map((pro, index) => (
                      <Tag
                        key={index}
                        color="green"
                        style={{ marginBottom: 4 }}
                      >
                        {pro}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {review.cons.length > 0 && (
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    缺点：
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    {review.cons.map((con, index) => (
                      <Tag key={index} color="red" style={{ marginBottom: 4 }}>
                        {con}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <MessageOutlined />
            <span>
              {t('user_reviews.title')} ({total})
            </span>
          </Space>
          {isAuthenticated && (
            <Button type="primary" onClick={() => openReviewModal()}>
              {t('user_reviews.write_review')}
            </Button>
          )}
        </div>
      }
    >
      {/* 简化后：移除评分统计，仅保留列表与分页 */}

      {/* 评论列表 */}
      <List
        loading={loading}
        dataSource={reviews}
        renderItem={renderReviewItem}
        locale={{ emptyText: t('user_reviews.empty') }}
      />

      {/* 分页 */}
      {total > 10 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Pagination
            current={currentPage}
            total={total}
            pageSize={10}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              t('user_reviews.pagination_total', {
                start: range[0],
                end: range[1],
                total,
              })
            }
          />
        </div>
      )}

      {/* 写评价弹窗 */}
      <Modal
        title={
          editingReview
            ? t('user_reviews.edit_review')
            : t('user_reviews.write_review')
        }
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          setEditingReview(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitReview}>
          <Form.Item
            name="content"
            label={t('myreviews.form_content_label')}
            rules={[
              { required: true, message: t('myreviews.content_required') },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t('myreviews.content_placeholder')}
            />
          </Form.Item>

          <Form.Item name="pros" label={t('user_reviews.pros_label')}>
            <TextArea rows={3} placeholder={t('myreviews.pros_placeholder')} />
          </Form.Item>

          <Form.Item name="cons" label={t('user_reviews.cons_label')}>
            <TextArea rows={3} placeholder={t('myreviews.cons_placeholder')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingReview
                  ? t('user_reviews.update_review')
                  : t('user_reviews.publish_review')}
              </Button>
              <Button
                onClick={() => {
                  setReviewModalVisible(false);
                  setEditingReview(null);
                  form.resetFields();
                }}
              >
                {t('user_reviews.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default UserReviews;
